import { useState, useEffect } from "react";

/**
 * Star Wars opening crawl shown ONCE per browser session the first time
 * the home screen mounts under the May 4th theme. The overlay covers the
 * full viewport, runs a 9.5s animation (intro → crawl → fade), then
 * unmounts itself. A "Skip" pill in the corner lets the user bail early.
 *
 * Session-scoped so a single user sees it once when they open the app
 * each day, but a new login or a fresh tab gets the cinematic again —
 * matches the user's intent ("first load each May 4th"). (User ask
 * 2026-05-04.)
 */
const SESSION_KEY = "may4CrawlSeen";
const TOTAL_DURATION_MS = 9500;

export default function May4Crawl({ storeName = "Wow Crew" }) {
  // Read sync from sessionStorage so we never paint the overlay if it has
  // already been seen this session — avoids a flash.
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) !== "1"; } catch { return false; }
  });

  useEffect(() => {
    if (!open) return;
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    const t = setTimeout(() => setOpen(false), TOTAL_DURATION_MS);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const skip = () => setOpen(false);

  return (
    <div className="may4-crawl-overlay" onClick={skip} role="presentation" data-testid="may4-crawl-overlay">
      <div className="may4-crawl-intro" data-testid="may4-crawl-intro">
        A long time ago in a store far,&nbsp;far&nbsp;away…
      </div>

      <div className="may4-crawl-stage" aria-hidden="true">
        <div className="may4-crawl" data-testid="may4-crawl">
          <h3>EPISODE 5·4</h3>
          <h3>THE WOW AWAKENS</h3>
          <p>
            Across the galaxy, the {storeName} prepares for a new day of
            captures. With every email saved, the Force grows stronger —
            and the Empire of Lost Customers retreats one tap at a time.
          </p>
          <p>
            Brave members of the WOW Crew rise to the challenge: tap the
            big plus, hit the goal, hold the line. The fate of the store's
            quarterly numbers rests in their capable hands.
          </p>
          <p>
            May the Force&hellip; and the customers&hellip; be with you.
          </p>
        </div>
      </div>

      <button
        type="button"
        className="may4-crawl-skip"
        onClick={(e) => { e.stopPropagation(); skip(); }}
        data-testid="may4-crawl-skip"
        aria-label="Skip intro"
      >
        Skip ▸
      </button>
    </div>
  );
}
